import { useCallback, useEffect, useRef, useState } from 'react';
import { GetGeoFenceStops } from '@/apis/shipment';
import type { GeoFenceStop } from '@/apis/types';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';

interface UseFetchGeoFenceStopsReturn {
  stops: GeoFenceStop[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetchGeoFenceStops: () => void;
}

const unwrapGeoFenceStops = (
  response: unknown
): { stops: GeoFenceStop[]; pageCount: number } => {
  const payload = response as {
    data?: {
      page_count?: number;
      data?: {
        stops?: GeoFenceStop[];
      };
    };
  };
  const wrappedPayload = response as {
    data?: {
      data?: {
        page_count?: number;
        data?: {
          stops?: GeoFenceStop[];
        };
      };
    };
  };
  const directPage = payload?.data;
  const wrappedPage = wrappedPayload?.data?.data;
  const stops = wrappedPage?.data?.stops ?? directPage?.data?.stops ?? [];
  const pageCount = wrappedPage?.page_count ?? directPage?.page_count ?? 1;

  return { stops, pageCount };
};

const useFetchGeoFenceStops = (
  shipmentId: string,
  enabled = true
): UseFetchGeoFenceStopsReturn => {
  const { checkTokenExpiry } = useToken();
  const { unauthorize } = useUnauthorize();
  const requestInProgressRef = useRef(false);
  const [stops, setStops] = useState<GeoFenceStop[]>([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const hasMore = enabled && page < pageCount;

  const fetchStops = useCallback(
    (pageNumber: number) => {
      const token = checkTokenExpiry();
      if (!enabled || !token || !shipmentId) {
        setStops([]);
        setPage(1);
        setPageCount(1);
        return;
      }
      if (requestInProgressRef.current) return;

      requestInProgressRef.current = true;
      setIsLoading(true);
      GetGeoFenceStops(token, shipmentId, pageNumber)
        .then((response) => {
          const { stops: nextStops, pageCount: nextPageCount } =
            unwrapGeoFenceStops(response);
          setStops((current) =>
            pageNumber === 1 ? nextStops : [...current, ...nextStops]
          );
          setPageCount(nextPageCount);
        })
        .catch((error) => {
          unauthorize(error);
          if (pageNumber === 1) setStops([]);
        })
        .finally(() => {
          setIsLoading(false);
          requestInProgressRef.current = false;
        });
    },
    [checkTokenExpiry, enabled, shipmentId, unauthorize]
  );

  const refetchGeoFenceStops = useCallback(() => {
    setPage(1);
    fetchStops(1);
  }, [fetchStops]);

  const loadMore = useCallback(() => {
    if (requestInProgressRef.current || !hasMore) return;
    setPage((current) => current + 1);
  }, [hasMore]);

  useEffect(() => {
    fetchStops(page);
  }, [fetchStops, page]);

  useEffect(() => {
    if (enabled) return;
    setStops([]);
    setPage(1);
    setPageCount(1);
  }, [enabled]);

  return { stops, isLoading, hasMore, loadMore, refetchGeoFenceStops };
};

export default useFetchGeoFenceStops;
